<template>
  <div id="app">
    <vc-nav-drawer v-resize="resize" :dark="false" :fixed="fixed" :temporary="temporary" application v-model="open">
      <div :class="['align-center', 'logo-icon']">
        <a href="/">
          <vc-icon :color="primaryColor" size="x-large">mdi-guitar-pick</vc-icon>
        </a>
      </div>
      <vc-list>
        <vc-list-item tag="a" v-for="(item, index) in menu" icon="chevron_right" :avatar="item.icon" :target="item.target || '_self'" :to="item.to" :key="index">
          <vc-list-item-primary-text>{{ item.label }}</vc-list-item-primary-text>
        </vc-list-item>
      </vc-list>
    </vc-nav-drawer>
    <vc-toolbar fixed tag="header" :color="primaryColor" application :class="['app-bar']">
      <vc-toolbar-icon-left icon="menu" @click="open = !open" />
      <vc-toolbar-title>Vue Chords</vc-toolbar-title>
      <vc-toolbar-controls-right>
        <vc-button v-for="(link, index) in toolbarLinks" v-if="toolbarLinks.length" icon-button :tag="link.external ? 'a' : 'router-link'" :target="link.external ? '_blank' : '_self'" :key="index" :to="link.to" :color="primaryColor" :title="link.hint" :class="['ml-0']">
          <vc-icon>{{ link.icon }}</vc-icon>
        </vc-button>
      </vc-toolbar-controls-right>
    </vc-toolbar>
    <vc-container tag="main" application>
      <vc-layout :spacing="8">
        <vc-col :span="24">
          <h1 :class="display1">{{ siteTitle }}</h1>
          <p>VcChordDiagram is an open source guitar chord diagram generator for the <a href="https://vuejs.org" target="_blank">VueJs</a> JavaScript framework,
            wrapped as an ES6 <a href="#getting-started">module</a> and available on <a href="https://www.npmjs.com/package/vc-chord-diagram" target="_blank">NPM</a>,
            <a href="https://yarnpkg.com/en/package/vc-chord-diagram" target="_blank">Yarn</a> and on jsDelivr and UnPkg CDN. It is fully customizable and supports different number of
          strings and tunings.</p>
        </vc-col>
        <vc-col :span="24">
          <h3 :class="title" id="demos">Demos</h3>
        </vc-col>
        <vc-col lg12 md24 :span="12" :class="['align-center']">
          <vc-layout>
            <vc-col :span="24">
              <h3 :class="subheading">Rendered as HTML</h3>
            </vc-col>
            <vc-col :class="['align-center']" v-for="(sample, index) in samples" :key="index" :span="8" md4 sm8 xs12>
              <vc-chord-diagram :title="sample.chord" :color="sample.color" :description="`Description of the ${sample.chord} chord`" :width="width" :height="height" :setup="chords[sample.chord]" />
            </vc-col>
          </vc-layout>
        </vc-col>
        <vc-col lg12 md24 :span="12" :class="['align-center']">
          <vc-layout>
            <vc-col :span="24">
              <h3 :class="subheading">Rendered as Images</h3>
            </vc-col>
            <vc-col v-for="(sample, index) in samples" :key="index" :span="8" md4 sm8 xs12>
              <vc-chord-diagram :title="sample.chord" :color="sample.color" :description="`Description of the ${sample.chord} chord`" :width="width" :height="height" :canvas="true" :setup="chords[sample.chord]" />
            </vc-col>
          </vc-layout>
        </vc-col>
        <vc-col md24 :span="24" id="getting-started">
          <vc-layout :spacing="8">
            <vc-col :span="24">
              <h2 :class="title">Installation</h2>
            </vc-col>
            <vc-col :span="24">
              <h3 :class="subheading">NPM</h3>
              <code>$ npm install vue-chord-diagram --save</code>
              <h3 :class="subheading">Yarn</h3>
              <code>$ yarn add vue-chord-diagram</code>
              <h3 :class="subheading">jsDelivr CDN</h3>
              <code>
                <span :class="['font-grey']">&lt;!-- stylesheet --&gt;</span><br />
                &lt;link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/vuecidity@/dist/lib/vuecidity.min.css" /&gt;<br />
                <span :class="['font-grey']">&lt;!-- vuejs --&gt;</span><br />
                &lt;script src="https://cdn.jsdelivr.net/npm/vue@2/dist/vue.min.js"&gt;&lt;/script&gt;<br />
                <span :class="['font-grey']">&lt;!-- vc-chord-diagram --&gt;</span><br />
                &lt;script src="https://cdn.jsdelivr.net/npm/vc-chord-diagram"&gt;&lt;/script&gt;<br />
                <span :class="['font-grey']">&lt;!-- or --&gt;</span><br />
                &lt;script src="https://cdn.jsdelivr.net/npm/vc-chord-diagram/dist/lib/vc-chord-diagram.min.js"&gt;&lt;/script&gt;<br />
              </code>
              <h3 :class="subheading">UnPkg CDN</h3>
              <code>
                <span :class="['font-grey']">&lt;!-- stylesheet --&gt;</span><br />
                &lt;link rel="stylesheet" href="https://unpkg.com/vc-chord-diagram/dist/lib/vc-chord-diagram.min.css" /&gt;<br />
                <span :class="['font-grey']">&lt;!-- vuejs --&gt;</span><br />
                &lt;script src="https://unpkg.com/vue/dist/vue.js"&gt;&lt;/script&gt;<br />
                <span :class="['font-grey']">&lt;!-- vc-chord-diagram --&gt;</span><br />
                &lt;script src="https://unpkg.com/vc-chord-diagram/dist/lib/vc-chord-diagram.min.js"&gt;&lt;/script&gt;<br />
              </code>
            </vc-col>
            <vc-col :span="24">
              <h2 :class="title">Dependencies</h2>
              <p>VueChordDiagram requires <a href="https://vuejs.org/" target="_blank">VueJs</a>
                and <a href="https://www.npmjs.com/package/html-to-image" target="_blank">HtmlToImage</a>,
                which are included in <code>package.json</code>.</p>
            </vc-col>
          </vc-layout>
        </vc-col>
        <vc-col md24 :span="24" id="docs">
          <h2 :class="title">Documentation</h2>
          <p>After installation, you need to include the <code>vc-chord-diagram</code> module and the stylesheet
            to your project.</p>
          <code>
            <span :class="['font-grey']">// import the module</span><br />
            import VcChordDiagram from 'vc-chord-diagram'<br />
            <span :class="['font-grey']">// import the stylesheet</span><br />
            import '../node_modules/vc-chord-diagram/dist/lib/vc-chord-diagram.min.css'<br />
            <span :class="['font-grey']">// add to Vue</span><br />
            Vue.use(VcChordDiagram)<br />
            <span :class="['font-grey']">// or add it to another Vue component</span><br />
            components: {<br />
            &nbsp;&nbsp;VcChordDiagram<br />
            }
          </code>
          <h3 :class="subheading">Usage</h3>
          <p>To create a single chord diagram, add the <code>vc-chord-diagram</code>
            tag to your page and customize it via the attributes below
            (only the <code>setup</code> attribute is required).</p>
          <code>
            &lt;vc-chord-diagram /&gt;
          </code>
          <h3 :class="subheading">Attributes</h3>
          <p>Below are the attributes that can be used to customize the look and feel of the component.</p>
          <ul :class="['font-grey', 'font-l-600', 'small']">
            <li>
              <code>setup</code> Required attribute (<code>Object</code>), contains the chord setup, fingers, position and tuning.
              You can add as many strings as you want. Regardless of the <code>startAt</code> position property,
              you should always count the positions as if they start from the first fret. Example of a <code>setup</code> object:
              <pre><code>{
  startAt: 1, <span :class="['font-grey']">// position to start the chord at</span>
  tuning: ['E', 'A', 'D', 'G', 'B', 'E'], <span :class="['font-grey']">// tuning</span>
  strings: [{ <span :class="['font-grey']">// strings array, starting from the lowest string</span>
    fret: 0, <span :class="['font-grey']">// fret (regardless of the startAt, frets should always start from 0)</span>
    finger: 'x' <span :class="['font-grey']">// finger ('x' means muted, '0' - open string)</span>
  }, {
    fret: 0,
    finger: 0
  }, {
    fret: 2,
    finger: 3
  }, {
    fret: 2,
    finger: 2
  }, {
    fret: 1,
    finger: 1
  }, {
    fret: 0,
    finger: 0
  }]
}</code></pre>
            </li>
            <li>
              <code>canvas</code> Optional attribute (<code>Boolean</code>), default value is <code>false</code>.
              If set to <code>true</code>,
              the chord diagram will render as an image, otherwise as HTML.
            </li>
            <li>
              <code>title</code> Optional attribute (<code>String</code>), chord title/name.
            </li>
            <li>
              <code>description</code> Optional attribute (<code>String</code>), chord description.
            </li>
            <li>
              <code>fingers</code> Optional attribute (<code>Boolean</code>), toggle the visibility of the fingers bar
              at the bottom of the diagram
            </li>
            <li>
              <code>width</code> Optional attribute (<code>String</code>), width of the diagram in pixels of percentage.
              The default value is <code>'160px'</code>.
            </li>
            <li>
              <code>height</code> Optional attribute (<code>String</code>), height of the diagram in pixels. The default
              value is <code>'100px'</code>.
            </li>
            <li>
              <code>notes</code> Optional attribute (<code>Boolean</code>), toggle the visibility of the nodes/keys bar
              at the top of the diagram. The default value is <code>true</code>.
            </li>
            <li>
              <code>position</code> Optional attribute (<code>Boolean</code>), toggle the visibility of the position bar
              at the left of the diagram. The default value is <code>true</code>.
            </li>
            <li>
              <code>color</code> Optional attribute (<code>String</code>), hex, rgb(a) or named color of the diagram.
              The default value is <code>'#000'</code>.
            </li>
            <li>
              <code>bg-color</code> Optional attribute (<code>String</code>), hex, rgb(a) or named background color
              of the diagram. The default value is <code>'#fff'</code>.
            </li>
            <li>
              <code>dot-size</code> Optional attribute (<code>Number</code>), finger dot size in pixels. The default
              value is <code>10</code> (pixels).
            </li>
          </ul>
        </vc-col>
      </vc-layout>
    </vc-container>
    <vc-footer narrow :color="primaryColor" align="right">
      &copy; {{ year }} Component, documentation and demo site built with <a href="https://vuecidity.wemakesites.net/" target="_blank">Vuecidity</a> for
      <a href="https://vuejs.org/" target="_blank">VueJs</a> by <a href="https://wemakesites.net/" target="_blank">Martin Ivanov</a>
    </vc-footer>
    <vc-social-bar size="small" :button-color="primaryColor" :fixed="true" :items="social"/>
  </div>
</template>
<script>
import VcChordDiagram from './components/vc-chord-diagram/index'
import Social from './social'

export default {
  name: 'app',
  components: {
    VcChordDiagram
  },
  data () {
    return {
      social: Social,
      open: true,
      fixed: true,
      temporary: false,
      year: new Date().getFullYear(),
      primaryColor: 'indigo',
      width: '100px',
      height: '160px',
      menu: [{
        label: 'Demos',
        to: '#demos',
        icon: 'fa-rocket'
      }, {
        label: 'Getting Started',
        to: '#getting-started',
        icon: 'hourglass_empty'
      }, {
        label: 'Docs',
        to: '#docs',
        icon: 'mdi-view-dashboard'
      }, {
        label: 'Donate',
        to: 'https://www.paypal.me/acidmartin',
        icon: 'fa-paypal',
        target: '_blank'
      }, {
        label: 'Contact Me',
        to: 'https://wemakesites.net/contact',
        target: '_blank',
        icon: 'email'
      }, {
        label: 'Vuecidity for VueJs',
        to: 'https://vuecidity.wemakesites.net',
        target: '_blank',
        icon: 'mdi-settings'
      }],
      googleAnalyticsTrackingId: 'UA-17572030-23',
      toolbarLinks: [{
        to: 'https://twitter.com/vuecidity',
        icon: 'fa-twitter',
        external: true,
        hint: 'Follow @vuecidity'
      }, {
        to: 'https://www.paypal.me/acidmartin',
        icon: 'fa-paypal',
        external: true,
        hint: 'Support the development of VcChordDiagram'
      }, {
        to: 'https://martinivanov.net',
        icon: 'mdi-wordpress',
        external: true,
        hint: 'Blog'
      }, {
        to: 'https://bitbucket.org/acidmartin/vc-chord-diagram',
        icon: 'mdi-bitbucket',
        external: true,
        hint: 'Bitbucket repo'
      }],
      samples: [{
        chord: 'Am',
        color: '#f44336'
      }, {
        chord: 'F',
        color: '#e91e63'
      }, {
        chord: 'A',
        color: '#673ab7'
      }, {
        chord: 'Am',
        color: '#3f51b5'
      }, {
        chord: 'F',
        color: '#009688'
      }, {
        chord: 'A',
        color: '#ff5722'
      }],
      siteTitle: 'Guitar Chord Diagrams for the VueJs JavaScript Framework',
      display1: ['display-1', 'font-grey', 'font-l-600'],
      subheading: ['subheading', 'font-grey', 'font-l-600'],
      title: ['title', 'font-grey', 'font-l-600'],
      chords: {
        Am: {
          startAt: 1,
          tuning: ['E', 'A', 'D', 'G', 'B', 'E'],
          strings: [{
            fret: 0,
            finger: 'x'
          }, {
            fret: 0,
            finger: 0
          }, {
            fret: 2,
            finger: 3
          }, {
            fret: 2,
            finger: 2
          }, {
            fret: 1,
            finger: 1
          }, {
            fret: 0,
            finger: 0
          }]
        },
        F: {
          startAt: 1,
          tuning: ['E', 'A', 'D', 'G', 'B', 'E'],
          strings: [{
            fret: 1,
            finger: 1
          }, {
            fret: 3,
            finger: 3
          }, {
            fret: 3,
            finger: 4
          }, {
            fret: 2,
            finger: 2
          }, {
            fret: 1,
            finger: 1
          }, {
            fret: 1,
            finger: 1
          }]
        },
        A: {
          startAt: 5,
          tuning: ['E', 'A', 'D', 'G', 'B', 'E'],
          strings: [{
            fret: 1,
            finger: 1
          }, {
            fret: 3,
            finger: 3
          }, {
            fret: 3,
            finger: 4
          }, {
            fret: 2,
            finger: 2
          }, {
            fret: 1,
            finger: 1
          }, {
            fret: 1,
            finger: 1
          }]
        }
      }
    }
  },
  methods: {
    resize () {
      const appElements = document.querySelectorAll('.vc-application:not(.vc-nav-drawer):not(.vc-loading-overlay)')
      const width = this.viewport().width
      if (width <= 960) {
        this.open = false
        this.temporary = true
        this.fixed = false
        appElements.forEach(element => {
          element.removeAttribute('style')
        })
      } else if (this.viewport().width >= 961) {
        this.open = true
        this.temporary = false
        this.fixed = true
      }
    },
    viewport () {
      const w = window
      const d = document
      const e = d.documentElement
      const g = d.getElementsByTagName('body')[0]
      const width = w.innerWidth || e.clientWidth || g.clientWidth
      const height = w.innerHeight || e.clientHeight || g.clientHeight
      return {
        width,
        height
      }
    }
  }
}
</script>
<style>
body
{
  font-family: Roboto, Avenir, Arial, "Helvetica Neue", Helvetica, sans-serif;
  cursor: default;
}

main
{
  margin: 48px 0;
}

.logo-icon
{
  margin: 64px 0;
}

.logo-icon a
{
  text-decoration: none;
}

.logo-icon .vc-icon
{
  transform: scale(3);
}

.small
{
  font-size: 13px;
}
</style>


// WEBPACK FOOTER //
// app.vue?0eb56d9e